import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { PageContainer } from "@/components/flowly/PageContainer/PageContainer";
import { useInView } from "@/hooks/useInView";
import { COUNT_UP_DURATION_MS, SOCIAL_STATS, type SocialStat } from "./constants";
import { useCountUp } from "./useCountUp";

// Thin "credibility band" placed directly under the hero. Three brand-gradient
// numbers + muted labels. Numbers count up from 0 once the strip scrolls into
// view; reduced-motion users get the final values instantly.
export function SocialProofStrip() {
  const { t, isRTL } = useLanguage();
  const { ref, isInView } = useInView();

  return (
    <section className="relative bg-card border-y border-border py-8 md:py-10">
      <PageContainer width="wide">
        <div
          ref={ref}
          className={cn(
            "grid grid-cols-3 gap-4 md:gap-12 items-center transition-all duration-700",
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
            isRTL && "rtl:[direction:rtl]",
          )}
        >
          {SOCIAL_STATS.map((stat, idx) => (
            <Stat key={stat.labelKey} stat={stat} isInView={isInView} delayMs={idx * 100} />
          ))}
        </div>
      </PageContainer>
    </section>
  );
}

interface StatProps {
  stat: SocialStat;
  isInView: boolean;
  delayMs: number;
}

function Stat({ stat, isInView, delayMs }: StatProps) {
  const { t } = useLanguage();
  const label = t.landing[stat.labelKey];
  const Icon = stat.icon;

  return (
    <div
      className="flex flex-col items-center text-center transition-all duration-700"
      style={{ transitionDelay: `${delayMs}ms` }}
    >
      {/* Brand-tinted icon disc above the number. Matches the HowItWorks
          visual language (icon-in-disc) but smaller and subtler so it doesn't
          compete with the gradient number for attention. */}
      <span className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-brand/10 ring-1 ring-brand/20 mb-2 md:mb-3">
        <Icon className="w-4 h-4 md:w-5 md:h-5 text-brand" />
      </span>
      <span
        // tabular-nums prevents the gradient text from jittering horizontally
        // as digits change width during the count-up.
        className="text-3xl md:text-5xl font-bold leading-none tabular-nums bg-gradient-to-br from-brand to-brand-secondary bg-clip-text text-transparent"
      >
        {stat.kind === "count" ? <CountValue stat={stat} isActive={isInView} /> : stat.value}
      </span>
      <span className="mt-2 text-xs md:text-sm text-muted-foreground font-medium">
        {label}
      </span>
    </div>
  );
}

interface CountValueProps {
  stat: Extract<SocialStat, { kind: "count" }>;
  isActive: boolean;
}

function CountValue({ stat, isActive }: CountValueProps) {
  const value = useCountUp({
    target: stat.target,
    isActive,
    durationMs: COUNT_UP_DURATION_MS,
  });
  // en-US locale keeps the thousands separator as a comma in both HE and EN
  // (HE locale renders the same separator; explicit locale avoids surprises).
  return (
    <>
      {value.toLocaleString("en-US")}
      {stat.suffix ?? ""}
    </>
  );
}
